import { useQuery } from '@tanstack/react-query';
import { LocaleLink as Link } from '../i18n/locale-link';
import { useT } from '../i18n/locale-context';
import { useShellSearchParams } from '../lib/use-shell-search-params';
import { fetchIfiProjects, parseIfiSourceParam } from '../lib/catalog-client';
import { tenderPath } from '../lib/slug';

/**
 * #52 — «Проекты по ведомствам»: a public breakdown on /ifi grouping the current
 * IFI tenders under their executing ministry/agency, each project linking to its
 * tender. Renders nothing until there's at least one agency (no empty section on
 * a freshly-seeded layer). Public metadata only — no gated contact here.
 */
export function IfiProjectsPanel() {
  const t = useT();
  const [params] = useShellSearchParams();
  // Тот же разбор, что и у чип-строки: мусорное значение — это «нет фильтра»,
  // а не 400 от API.
  const ifiSource = parseIfiSourceParam(params.get('ifiSource'));
  const { data } = useQuery({
    queryKey: ['ifi-projects', ifiSource],
    queryFn: () => fetchIfiProjects(ifiSource),
  });

  // Пустой ответ значит разное. Без выбранного института это «слой ещё не
  // наполнен» — рисовать заголовок над пустотой незачем. А вот с институтом
  // это осмысленный факт: рядом стоит список лотов, и молча исчезнувшая секция
  // читается как потеря данных, хотя у источника ведомства просто нет
  // (UNGM публикует агентство ООН, а не узбекское ведомство).
  if (!data) return null;
  if (data.length === 0 && !ifiSource) return null;

  return (
    <section className="mt-10 border-t border-edge pt-6" aria-labelledby="ifi-projects-h">
      <h2 id="ifi-projects-h" className="text-[17px] font-bold text-ink">
        {t('ifi.projects.title')}
      </h2>
      <p className="mb-4 mt-1 text-[13px] text-ink-muted">
        {data.length === 0 ? t('ifi.projects.emptyForSource') : t('ifi.projects.subtitle')}
      </p>

      <div className="flex flex-col gap-5">
        {data.map((group) => (
          <div key={group.agency}>
            <h3 className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
              {group.agency}
            </h3>
            <ul className="flex flex-col gap-1.5 border-l border-edge-light pl-3">
              {group.projects.map((p) => (
                <li key={p.id}>
                  <Link
                    to={tenderPath(p.slug, p.id)}
                    className="text-[14px] font-medium text-ink-soft hover:text-brand"
                  >
                    {p.projectName ?? p.title}
                  </Link>
                  {p.projectName && p.projectName !== p.title && (
                    <span className="ml-1.5 text-[12px] text-ink-muted">{p.title}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
